import React from 'react';
import { WeezHeader, Card, ContentCard, FilterSetController, Filter, SelectButton, DropdownItem, CheckboxLabel, SelectHierarchy, ToastContext } from '@weezevent/nacre';
import { useTranslation } from 'react-i18next';
import TicketOfficeEventList from './ticketOfficeEventList';
import { useFilters } from '../../../manager/components/context';
import useFirstRender from '../../../../helpers/useFirstRender';
import { fetcher } from '@weezevent/weezjs-core';
import css from './ticketOfficeEvent.module.css';

const spec = {
    multiple: ['status', 'time_status'],
    fields: ['search'],
    aliases: {},
    date: []
};

export default function TicketOfficeEvent({ organization, ticket_office, syncTicketOffice }) {
    const { t } = useTranslation();
    const isFirstRender = useFirstRender();
    const toast = React.useContext(ToastContext);

    const initialFilters = { status: new Set(['draft', 'published']), time_status: new Set(['upcoming', 'ongoing', 'over']) };
    const [filters, setFilters] = useFilters(spec, initialFilters);

    const [selected, setSelected] = React.useState([]);

    const options = [
        { key: 'published', text: t('ticket.event.list.statusPublished'), name: 'published' },
        { key: 'draft', text: t('ticket.event.list.statusDraft'), name: 'draft' }
    ];

    const multiple_action = React.useCallback(
        type => {
            let pks = [];
            let events_id = [];
            selected.forEach(element => {
                if (element.event_ticket_office_id) {
                    pks.push(element.event_ticket_office_id);
                } else {
                    pks.push(element.id);
                }
            });
            let data = {
                pks: pks,
                events_id: events_id
            };
            fetcher
                .post(`/ticket/organizations/${organization.id}/ticket_office/${ticket_office.id}/${type}_multiple_visibility`, { data: data })
                .then(() => {
                    toast.success(t('ticket.ticket_office_rates.toast-success'));
                    syncTicketOffice({
                        allow_cache: false,
                        cache: false
                    });
                })
                .catch(() => {
                    toast.error(t('ticket.ticket_office_rates.toast-error'));
                });
        },
        [organization, ticket_office, selected, t, toast]
    );

    const handleSearch = React.useCallback(
        value => {
            let _filters = { ...filters };
            if (!value || value === '') {
                delete _filters['search'];
                return setFilters(_filters);
            }
            _filters['search'] = value;
            return setFilters(_filters);
        },
        [filters]
    );

    const handleFilters = React.useCallback(
        values => {
            if (filters.search !== undefined && filters.search.length > 0) {
                values.search = filters.search;
            } else {
                delete values.search;
            }

            if ('time_status' in filters && filters.time_status.size) {
                values.time_status = filters.time_status;
            }

            if (('time_status' in values && !values.time_status.size) || (!isFirstRender && !filters.time_status && 'time_status' in values)) {
                delete values['time_status'];
            }
            setFilters(values);
        },
        [filters, isFirstRender]
    );

    const filteredEvents = React.useMemo(() => {
        const search = { ...filters }.search;

        if (!ticket_office.events.length) {
            return [];
        }

        let _events = ticket_office.events;

        if (search) {
            _events = _events.filter(ev => ev.name.toLowerCase().includes(search.toLowerCase()) || ev.id.toString().includes(search));
        }
        if ('status' in filters && Array.from(filters.status).length) {
            _events = _events.filter(ev => Array.from(filters.status).includes(ev.status));
        }
        if ('time_status' in filters && Array.from(filters.time_status).length) {
            _events = _events.filter(ev => Array.from(filters.time_status).includes(ev.time_status));
        }

        return _events.length ? _events : [];
    }, [filters, ticket_office]);

    return (
        <>
            <WeezHeader title={t('ticket.ticket_offices.events.pageTitle')}>{t('ticket.ticket_offices.events.header_description')}</WeezHeader>

            <Card>
                <ContentCard>
                    <FilterSetController
                        total={ticket_office.events && filteredEvents.length}
                        labelTotal={t('ticket.ticket_offices.events.label_count')}
                        labelReset={t('ticket.common.action.reset')}
                        labelFilters={t('ticket.common.cta.filters')}
                        search={{
                            displayResetIcon: true,
                            placeholder: t('ticket.common.action.search'),
                            value: filters.search || '',
                            onChange: (synth, value) => {
                                handleSearch(value);
                            },
                            handleReset: () => {
                                handleSearch('');
                            }
                        }}
                        onChange={values => handleFilters(values)}
                        initialValues={() => {
                            // Check if url already contains filters to apply, otherwise set initialfilters
                            if (isFirstRender && !Object.keys(filters).some(key => spec.fields.includes(key))) {
                                setFilters(initialFilters);
                                return initialFilters;
                            }
                            return filters;
                        }}
                        defaultValues={initialFilters}
                        onReset={() => setFilters(initialFilters)}
                    >
                        <Filter
                            index="1"
                            multiple
                            labelSelectAll={t('ticket.common.action.select-all')}
                            labelName={t('ticket.accounting.filter.statusLabel')}
                            labelCTA={t('ticket.common.action.apply')}
                            name="status"
                            options={options}
                        />
                    </FilterSetController>
                    <SelectButton
                        title={<LabelActionButton selected={selected} />}
                        tooltipTitle={t('ticket.ticket_offices.events.action.noActionTooltip')}
                        objects={Object.values(selected)}
                    >
                        <DropdownItem item={t('ticket.ticket_offices.events.action.display')} onClick={() => multiple_action('enable')} />
                        <DropdownItem item={t('ticket.ticket_offices.events.action.hide')} onClick={() => multiple_action('disable')} />
                    </SelectButton>

                    <div className={css['filter-boxes']}>
                        {/* // TODO Implement and debug time_status checkboxes */}
                        <TimeStatusToggles filters={filters} setFilters={setFilters} />
                    </div>
                </ContentCard>
                <TicketOfficeEventList
                    organization={organization}
                    events={filteredEvents}
                    ticket_office={ticket_office}
                    selected={selected}
                    setSelected={setSelected}
                    syncTicketOffice={syncTicketOffice}
                />
            </Card>
        </>
    );
}

function LabelActionButton({ selected = {} }) {
    const { t } = useTranslation();

    if (!selected || Object.values(selected).length === 0) {
        return <span>{t('ticket.common.action.bulk')}</span>;
    }

    return (
        <span>
            {t('ticket.ticket_offices.events.action.events_selected', { count: Object.values(selected).length })}
            <span className={css['label-events-action']}>{` ${t('ticket.common.action.bulk-selected')}`}</span>
        </span>
    );
}

function TimeStatusToggles({ filters, setFilters }) {
    const { t } = useTranslation();

    const [width, setWidth] = React.useState(window.innerWidth);
    const breakpointMobile = 600;
    const breakpointTablet = 960;

    const filterOptions = React.useMemo(() => {
        const options = [
            { text: t('ticket.common.quickFilters.upcoming'), value: 'upcoming' },
            { text: t('ticket.common.quickFilters.ongoing'), value: 'ongoing' },
            { text: t('ticket.common.quickFilters.over'), value: 'over' }
        ];
        return options;
    }, []);

    const viewType = React.useMemo(() => {
        if (width <= breakpointMobile) {
            return 'mobile';
        }
        if (width >= breakpointTablet) {
            return 'desktop';
        }
        return 'tablet';
    }, [width]);

    const updateTimeStatusFilters = React.useCallback(
        (name, value) => {
            let previousFilters = { ...filters };
            if (!value.size) {
                delete previousFilters[name];
                return setFilters(previousFilters);
            }
            let newFilters = {
                ...previousFilters,
                [name]: value
            };
            setFilters(newFilters);
        },
        [filters]
    );

    // Onchange time status filter desktop view
    const handleTimeStatusToggle = React.useCallback(
        (value, checked) => {
            let checks = filters.time_status || new Set([]);

            if (checked) {
                checks.add(value);
            } else {
                checks.delete(value);
            }
            updateTimeStatusFilters('time_status', checks);
        },
        [filters, updateTimeStatusFilters]
    );

    // Onchange time status filter mobile view
    const handleTimeStatusHierarchy = React.useCallback(
        selected => {
            updateTimeStatusFilters('time_status', new Set(selected));
        },
        [updateTimeStatusFilters]
    );

    const selectedTimeStatusFilters = React.useMemo(() => {
        return filters.time_status ? Array.from(filters.time_status) : [];
    }, [filters]);

    React.useEffect(() => {
        const handleResizeWindow = () => setWidth(window.innerWidth);
        window.addEventListener('resize', handleResizeWindow);
        return () => {
            window.removeEventListener('resize', handleResizeWindow);
        };
    }, []);

    if (viewType === 'mobile') {
        return (
            <div className={css['filters-hierarchy']}>
                <SelectHierarchy
                    multi
                    height={'mini'}
                    placeholder={t('ticket.common.action.display')}
                    options={filterOptions}
                    key={selectedTimeStatusFilters}
                    selected={selectedTimeStatusFilters}
                    onChange={handleTimeStatusHierarchy}
                    totalItems={filterOptions.length}
                    labelSelectAll={t('ticket.common.action.select-all')}
                    labelApply={t('ticket.common.action.apply')}
                />
            </div>
        );
    }
    return (
        <div className={css['filter-checkboxes']}>
            <span className={css['filter-boxes-label']}>{`${t('ticket.common.action.display')}: `}</span>
            <CheckboxLabel
                label={t('ticket.common.quickFilters.upcoming')}
                checked={filters.time_status?.has('upcoming')}
                onChange={(e, st) => {
                    handleTimeStatusToggle('upcoming', st.checked);
                }}
            />
            <CheckboxLabel
                label={t('ticket.common.quickFilters.ongoing')}
                checked={filters.time_status?.has('ongoing')}
                onChange={(e, st) => {
                    handleTimeStatusToggle('ongoing', st.checked);
                }}
            />
            <CheckboxLabel
                label={t('ticket.common.quickFilters.over')}
                checked={filters.time_status?.has('over')}
                onChange={(e, st) => {
                    handleTimeStatusToggle('over', st.checked);
                }}
            />
        </div>
    );
}
